import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Users, TrendingUp, TrendingDown, CircleDollarSign, Ban, RefreshCw, AlertTriangle, Mail, MessageCircle, Ticket } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const STATUS_LABEL = {
  active: "مشترك نشط",
  canceling: "سيُلغى",
  past_due: "متأخر السداد",
  canceled: "ملغى",
  revoked: "موقوف",
  pending: "معلق",
};
const STATUS_CLASS = {
  active: "bg-green-100 text-green-800",
  canceling: "bg-amber-100 text-amber-900",
  past_due: "bg-red-100 text-red-800",
  canceled: "bg-slate-100 text-slate-600",
  revoked: "bg-red-50 text-red-700 line-through",
  pending: "bg-blue-50 text-blue-800",
};

// Compact "من أين جاء؟" cell — shows the buyer's acquisition source at a glance.
// Order of preference: click attribution → coupon attribution → funnel-only → مباشر.
function SourceCell({ attribution, funnel }) {
  const FUNNEL_LABEL = { flex: "المرونة", homeworkout: "التمارين المنزلية" };
  const CHANNEL_META = {
    email:    { icon: Mail,          label: "بريد",   cls: "bg-blue-50 text-blue-800 border-blue-200" },
    whatsapp: { icon: MessageCircle, label: "واتساب", cls: "bg-green-50 text-green-800 border-green-200" },
  };
  const funnelBadge = funnel && (
    <span className="text-[9px] text-foreground/50 ms-1">
      {FUNNEL_LABEL[funnel] || funnel}
    </span>
  );

  if (!attribution || !attribution.channel) {
    // No click / no coupon signal — buyer came directly (typed URL, bookmarked, etc.)
    return (
      <span className="text-[10px] text-foreground/50 inline-flex items-center gap-1">
        مباشر {funnelBadge}
      </span>
    );
  }

  const meta = CHANNEL_META[attribution.channel] || {
    label: attribution.channel, cls: "bg-foreground/5 text-foreground/70 border-foreground/20",
  };
  const Icon = meta.icon;
  const stepShort = attribution.step ? attribution.step.replace("step_", "#") : "";
  return (
    <div className="flex flex-col items-center gap-0.5" title={`القناة: ${attribution.channel}\nالحملة: ${attribution.campaign || "—"}\nالخطوة: ${attribution.step || "—"}`}>
      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${meta.cls}`} data-testid="source-badge">
        {Icon && <Icon className="h-2.5 w-2.5" />}
        <span className="font-medium">{meta.label}</span>
        {stepShort && <span className="opacity-60">{stepShort}</span>}
      </span>
      {attribution.source === "coupon" && attribution.coupon_code && (
        <span className="text-[9px] text-orange-700 inline-flex items-center gap-0.5">
          <Ticket className="h-2.5 w-2.5" />{attribution.coupon_code}
        </span>
      )}
      {funnelBadge}
    </div>
  );
}

export default function AdminSubscriptions() {
  const [analytics, setAnalytics] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("any");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const pageSize = 25;

  const loadAnalytics = async () => {
    try {
      const { data } = await api.get("/admin/subscriptions/analytics");
      setAnalytics(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل التحليلات");
    }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscriptions/list", {
        params: { q, status: filter, page, page_size: pageSize },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل القائمة");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAnalytics(); }, []);
  useEffect(() => { loadList(); }, [q, filter, page]);

  const cancelSub = async (row) => {
    if (!confirm(`إلغاء اشتراك ${row.name || row.email}؟\nسيبقى الوصول ساريًا حتى نهاية الفترة الحالية.`)) return;
    try {
      await api.post(`/admin/subscriptions/${row.purchase_id}/cancel`);
      toast.success("تم جدولة الإلغاء عند نهاية الفترة الحالية");
      loadAnalytics();
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإلغاء");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6 pt-2" dir="rtl" data-testid="admin-subscriptions">
      {/* KPI Cards */}
      {analytics && (
        <div className="flex justify-end mb-2">
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              if (!confirm("مزامنة المبالغ من Stripe؟\nسيتم قراءة آخر فاتورة مدفوعة لكل اشتراك وتصحيح المبلغ في التحليلات (يعالج الكوبونات المطبّقة).")) return;
              try {
                const { data } = await api.post("/admin/subscriptions/resync-amounts");
                toast.success(`صُحّح ${data.corrected} اشتراك من أصل ${data.processed}`);
                loadAnalytics();
                loadList();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "فشل التصحيح");
              }
            }}
            data-testid="btn-resync-amounts"
          >
            <RefreshCw className="h-4 w-4 me-1" /> مزامنة المبالغ من Stripe
          </Button>
        </div>
      )}

      {analytics && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6" data-testid="subs-kpis">
          <KpiCard
            label="المشتركون النشطون"
            value={(analytics.unique_customers?.active ?? analytics.totals.active) + (analytics.unique_customers?.canceling ?? analytics.totals.canceling)}
            hint={
              analytics.unique_customers?.canceling > 0
                ? `${analytics.unique_customers.canceling} منهم سيُلغى في نهاية دورته`
                : "لهم وصول فعلي الآن"
            }
            icon={Users}
            color="green"
            testid="kpi-active"
          />
          <KpiCard
            label="سيُلغون قريبًا"
            value={analytics.unique_customers?.canceling ?? analytics.totals.canceling}
            hint="طلبوا الإلغاء — سيُحتسبون في Churn عند انتهاء دوراتهم"
            icon={RefreshCw}
            color="amber"
            testid="kpi-canceling"
          />
          <KpiCard label="متأخرو الدفع" value={analytics.totals.past_due} icon={TrendingDown} color="red" testid="kpi-past-due" />
          <KpiCard label="ملغى / موقوف" value={(analytics.totals.canceled + analytics.totals.revoked)} icon={Ban} color="slate" testid="kpi-canceled" />
          <KpiCard label="MRR (ريال/شهر)" value={fmt(analytics.mrr_sar)} icon={CircleDollarSign} color="blue" testid="kpi-mrr" />
          <KpiCard label="ARR (ريال/سنة)" value={fmt(analytics.arr_sar)} icon={TrendingUp} color="primary" testid="kpi-arr" />
        </div>
      )}
      {analytics && analytics.totals.pending > 0 && (
        <div className="text-xs px-3 py-2 rounded bg-blue-50 border border-blue-200 text-blue-900" dir="rtl">
          <span className="font-semibold">{analytics.totals.pending} سجل معلق</span> — أشخاص بدأوا الشراء ولم يُكملوا الدفع (بطاقة رُفضت أو أُغلقت الصفحة).
          هذه السجلات لا تُحتسب ضمن الإيرادات ولا الاحتفاظ. يمكنك تصفيتها من قائمة الحالات أدناه.
        </div>
      )}

      {/* At-risk MRR — leading indicator specifically for annual businesses */}
      {analytics && analytics.at_risk_mrr_sar > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200" data-testid="at-risk-mrr-card" dir="rtl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-sm text-amber-900">تنبيه: إيراد شهري مُهدَّد</h3>
              <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                إجمالي <b>{analytics.at_risk_mrr_sar.toLocaleString("ar-SA")} SAR/شهر</b> مُعرَّض للفقدان من مشتركين طلبوا الإلغاء.
                هذا مؤشر مبكّر — الإلغاء الفعلي سيظهر في Churn عند انتهاء دورة كل مشترك.
              </p>
              {analytics.scheduled_cancels_forecast && (
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <ForecastPill label="خلال 30 يومًا" count={analytics.scheduled_cancels_forecast.next_30d.count} mrr={analytics.scheduled_cancels_forecast.next_30d.mrr_at_risk} />
                  <ForecastPill label="30-60 يومًا" count={analytics.scheduled_cancels_forecast.next_60d.count} mrr={analytics.scheduled_cancels_forecast.next_60d.mrr_at_risk} />
                  <ForecastPill label="60-90 يومًا" count={analytics.scheduled_cancels_forecast.next_90d.count} mrr={analytics.scheduled_cancels_forecast.next_90d.mrr_at_risk} />
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Churn + Retention */}
      {analytics && (
        <div className="grid gap-3 md:grid-cols-2" dir="rtl">
          <Card className="p-4 bg-white border-foreground/10">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-600" /> معدل الإلغاء (Churn Rate)
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-slate-50 rounded p-3">
                <div className="text-foreground/60 mb-1">آخر 30 يومًا</div>
                <div className="text-2xl font-bold" data-testid="churn-30">{analytics.churn_30d_pct}%</div>
                <div className="text-foreground/50 mt-1">
                  {analytics.canceled_in_last_30d} إلغاء من أصل {analytics.active_at_start_30d} مشترك
                </div>
              </div>
              <div className="bg-slate-50 rounded p-3">
                <div className="text-foreground/60 mb-1">آخر 90 يومًا</div>
                <div className="text-2xl font-bold" data-testid="churn-90">{analytics.churn_90d_pct}%</div>
                <div className="text-foreground/50 mt-1">
                  {analytics.canceled_in_last_90d} إلغاء من أصل {analytics.active_at_start_90d} مشترك
                </div>
              </div>
            </div>
            <p className="text-[10px] text-foreground/50 mt-2">
              Churn = عدد من ألغَوا فعليًا (انتهت اشتراكاتهم) / عدد النشطين في بداية الفترة. أقل معدل = أفضل.
            </p>
            {analytics.unique_customers?.canceling > 0 && (
              <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-2 leading-relaxed">
                ⚠️ يوجد <b>{analytics.unique_customers.canceling}</b> مشترك طلبوا الإلغاء لكنّ اشتراكهم لم ينتهِ بعد — سيُحتسبون هنا عند انتهاء دوراتهم الحالية (وليس الآن).
              </p>
            )}
          </Card>

          <Card className="p-4 bg-white border-foreground/10">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" /> نسبة البقاء (Retention)
            </h3>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {[
                { label: "بعد سنة", key: "year_1_pct", sample: "sample_1y" },
                { label: "بعد سنتين", key: "year_2_pct", sample: "sample_2y" },
                { label: "بعد 3 سنوات", key: "year_3_pct", sample: "sample_3y" },
              ].map((c) => (
                <div key={c.key} className="bg-green-50/50 rounded p-3 text-center" data-testid={`retention-${c.key}`}>
                  <div className="text-foreground/60 mb-1">{c.label}</div>
                  <div className="text-xl font-bold text-green-700">
                    {analytics.retention_overall[c.key] === null ? "—" : `${analytics.retention_overall[c.key]}%`}
                  </div>
                  <div className="text-[10px] text-foreground/50 mt-1">
                    {analytics.retention_overall[c.sample]} مشترك مؤهل
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-foreground/50 mt-2">
              نسبة البقاء = المشتركون الذين مضى عليهم N سنة وما زالوا نشطين. "المشترك المؤهل" = مضى على انضمامه N سنة على الأقل.
            </p>
          </Card>
        </div>
      )}

      {/* Cohort table */}
      {analytics && analytics.cohorts.length > 0 && (
        <Card className="p-4 bg-white border-foreground/10" data-testid="cohorts-card">
          <h3 className="font-semibold text-sm mb-1">جدول الأفواج (Cohort Retention)</h3>
          <p className="text-[10px] text-foreground/50 mb-3">كل صف = شهر انضمام. الأرقام تُحسب لكل شخص فريد (البريد المكرّر يُحتسب مرة واحدة) وتستثني السجلات المعلقة. عمود "سيُلغي" يكشف الإلغاءات القادمة قبل تنفيذها فعليًا.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-foreground/10 text-right">
                <tr>
                  <th className="p-2">شهر الانضمام</th>
                  <th className="p-2 text-center">عدد المنضمّين</th>
                  <th className="p-2 text-center">لا يزالون مشتركين</th>
                  <th className="p-2 text-center" title="مشتركون طلبوا الإلغاء وسينتهي اشتراكهم مستقبلاً">سيُلغى قريبًا</th>
                  <th className="p-2 text-center" title="متوسط عمر المشترك عند الإلغاء لهذا الفوج">متوسط عمر المُلغي</th>
                  <th
                    className="p-2 text-center"
                    title="مجموع كل ما دفعه هذا الفوج فعليًا حتى الآن — بكل السنين والدورات المُكتملة (وليس توقّع ARR). سيتجاوز الـ ARR بعد سنة كاملة من الاحتفاظ."
                  >
                    الإيراد الفعلي
                    <span className="block text-[9px] font-normal text-foreground/50 leading-none mt-0.5">(حتى الآن)</span>
                  </th>
                  <th className="p-2 text-center">بقاء سنة</th>
                  <th className="p-2 text-center">بقاء سنتين</th>
                  <th className="p-2 text-center">بقاء 3 سنوات</th>
                </tr>
              </thead>
              <tbody>
                {analytics.cohorts.slice().reverse().map((c) => (
                  <tr key={c.month} className="border-b border-foreground/5">
                    <td className="p-2 font-mono">{c.month}</td>
                    <td className="p-2 text-center">{c.started}</td>
                    <td className="p-2 text-center">
                      <span className="font-medium">{c.still_active}</span>
                      <span className="text-foreground/40"> / {c.started}</span>
                    </td>
                    <td className="p-2 text-center">
                      {c.scheduled_to_cancel > 0 ? (
                        <span className="text-amber-700 font-medium">{c.scheduled_to_cancel}</span>
                      ) : (
                        <span className="text-foreground/30">—</span>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      {c.canceled_count > 0
                        ? `${c.avg_lifetime_months_when_canceled} شهر`
                        : <span className="text-foreground/30">—</span>}
                    </td>
                    <td className="p-2 text-center font-mono">
                      {c.revenue_to_date > 0 ? Math.round(c.revenue_to_date).toLocaleString("ar-SA") : "—"}
                    </td>
                    <td className="p-2 text-center">{c.retention_1y_pct === null ? <span className="text-foreground/30">لم تكتمل السنة</span> : `${c.retention_1y_pct}%`}</td>
                    <td className="p-2 text-center">{c.retention_2y_pct === null ? <span className="text-foreground/30">—</span> : `${c.retention_2y_pct}%`}</td>
                    <td className="p-2 text-center">{c.retention_3y_pct === null ? <span className="text-foreground/30">—</span> : `${c.retention_3y_pct}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Churn timeline chart — Actual vs Predicted vs Effective */}
      {analytics && analytics.churn_timeline && analytics.churn_timeline.length > 0 && (
        <Card className="p-4 bg-white border-foreground/10" data-testid="churn-timeline-card">
          <h3 className="font-semibold text-sm mb-1">اتجاه معدل الإلغاء الشهري (12 شهرًا)</h3>
          <p className="text-[10px] text-foreground/50 mb-3">
            <b>الفعلي</b>: إلغاءات مكتملة · <b>المُخطَّط</b>: مشتركون طلبوا الإلغاء وسيُطبَّق مستقبلاً · <b>الإجمالي</b>: مجموعهما (المؤشر الأدق للتوقع).
            في الاشتراكات السنوية، "الفعلي" متأخر ~11 شهرًا — راقب خط "المُخطَّط" لتتوقع الموجة قبل حدوثها.
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={analytics.churn_timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v) => `${v}%`} labelStyle={{ direction: "rtl" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="actual_pct" name="الفعلي" stroke="#dc2626" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="predicted_pct" name="المُخطَّط" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" dot={false} />
              <Line type="monotone" dataKey="effective_pct" name="الإجمالي" stroke="#3b82f6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Recent cancels */}
      {analytics && analytics.recent_cancels.length > 0 && (
        <Card className="p-4 bg-white border-foreground/10" data-testid="recent-cancels-card">
          <h3 className="font-semibold text-sm mb-3 text-red-700">آخر إلغاءات (Top 10)</h3>
          <div className="space-y-1 text-xs">
            {analytics.recent_cancels.map((c, i) => (
              <div key={i} className="flex justify-between border-b border-foreground/5 py-1">
                <span>{c.name || c.email}</span>
                <span className="text-foreground/50">مشترك لمدة {c.months_active} شهر — {(c.canceled_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Subscribers list with filter + search + cancel */}
      <Card className="p-4 bg-white border-foreground/10" data-testid="subs-list-card">
        <div className="flex gap-2 flex-wrap items-center mb-3">
          <h3 className="font-semibold text-sm flex-1">
            قائمة المشتركين ({total} سجل
            {analytics?.unique_customers && filter === "any" && total !== analytics.unique_customers.all_time
              ? ` / ${analytics.unique_customers.all_time} شخص فريد`
              : ""})
          </h3>
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setPage(1); }}
            className="border rounded px-2 py-1.5 text-xs bg-white"
            data-testid="subs-filter"
          >
            <option value="any">جميع الحالات</option>
            <option value="active">نشط</option>
            <option value="canceling">سيُلغى</option>
            <option value="past_due">متأخر</option>
            <option value="pending">معلق (لم يكتمل الدفع)</option>
            <option value="canceled">ملغى</option>
            <option value="revoked">موقوف</option>
          </select>
          <form onSubmit={(e) => { e.preventDefault(); setPage(1); loadList(); }} className="flex gap-1">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم/البريد/الهاتف..." className="w-48 h-8 text-xs" data-testid="subs-search" />
            <Button size="sm" type="submit">بحث</Button>
          </form>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-foreground/10">
              <TableHead className="text-foreground">الاسم</TableHead>
              <TableHead className="text-foreground">البريد</TableHead>
              <TableHead className="text-foreground">الحالة</TableHead>
              <TableHead className="text-foreground text-center">المبلغ</TableHead>
              <TableHead className="text-foreground text-center">الفترة</TableHead>
              <TableHead className="text-foreground text-center">من أين جاء؟</TableHead>
              <TableHead className="text-foreground text-center">بدأ</TableHead>
              <TableHead className="text-foreground text-center">التجديد التالي</TableHead>
              <TableHead className="text-foreground text-center">أشهر النشاط</TableHead>
              <TableHead className="text-foreground">إجراء</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={10} className="text-center py-6 opacity-50">جاري التحميل...</TableCell></TableRow>}
            {!loading && items.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-6 opacity-50">لا نتائج</TableCell></TableRow>}
            {items.map((r) => {
              const st = r.subscription_status;
              return (
                <TableRow key={r.purchase_id} className="border-foreground/10 hover:bg-foreground/5">
                  <TableCell className="text-xs">{r.name || "—"}</TableCell>
                  <TableCell className="font-mono text-[11px]">{r.email}</TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${STATUS_CLASS[st] || "bg-slate-100"}`}>
                      {STATUS_LABEL[st] || st}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center text-xs">
                    {r.amount} {r.currency}
                  </TableCell>
                  <TableCell className="text-center text-[11px] text-foreground/60">
                    {r.sub_interval ? `${r.sub_interval_count || 1} ${intervalAr(r.sub_interval)}` : "—"}
                  </TableCell>
                  <TableCell className="text-center">
                    <SourceCell attribution={r.attribution} funnel={r.source_funnel} />
                  </TableCell>
                  <TableCell className="text-center text-[11px] text-foreground/60">{(r.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-center text-[11px] text-foreground/60">{(r.current_period_end || "").slice(0, 10) || "—"}</TableCell>
                  <TableCell className="text-center text-xs">{r.months_active}</TableCell>
                  <TableCell>
                    {(st === "active" || st === "past_due") && (
                      <Button size="sm" variant="outline" onClick={() => cancelSub(r)} data-testid={`btn-cancel-sub-${r.purchase_id}`}>
                        إلغاء
                      </Button>
                    )}
                    {st === "canceling" && <span className="text-[10px] text-amber-700">سيُلغى</span>}
                    {st === "canceled" && <span className="text-[10px] text-foreground/40">ملغى</span>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {total > pageSize && (
          <div className="flex justify-between items-center mt-3 text-xs">
            <span className="text-foreground/60">صفحة {page} من {totalPages}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="subs-prev">السابق</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="subs-next">التالي</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function ForecastPill({ label, count, mrr }) {
  return (
    <div className="rounded-md bg-white border border-amber-200 p-2 text-center">
      <div className="text-[10px] text-amber-800">{label}</div>
      <div className="font-bold text-amber-900">{count}</div>
      <div className="text-[10px] text-amber-700">-{Math.round(mrr).toLocaleString("ar-SA")} SAR/شهر</div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, testid, hint }) {
  const colorMap = {
    green: "bg-green-50 border-green-200 text-green-800",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    red: "bg-red-50 border-red-200 text-red-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    primary: "bg-primary/5 border-primary/30 text-primary",
  };
  return (
    <Card className={`p-3 border ${colorMap[color] || colorMap.slate}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" />
        <span className="text-[10px]">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {hint && <div className="text-[9px] opacity-70 mt-1 leading-tight">{hint}</div>}
    </Card>
  );
}

function fmt(n) {
  const num = Number(n) || 0;
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return Math.round(num).toLocaleString("en-US");
}

function intervalAr(i) {
  const m = { year: "سنة", month: "شهر", week: "أسبوع", day: "يوم" };
  return m[i] || i;
}
